import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Button,
  Alert,
  Spinner,
  Progress,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { toast } from "react-toastify";
import {
  getSetupStatus,
  initializeSetup,
  initializeKYC,
  checkBalance,
} from "../../api/airwallex";
import * as Airwallex from "@airwallex/components-sdk";
import { AIRWALLEX_CONFIG } from "../../config/airwallex";
import "./WalletSetup.scss";
import "./KYCModal.scss";

const WalletSetup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();


  const [setupStatus, setSetupStatus] = useState({
    status: null, // CREATED, SUBMITTED, ACTION_REQUIRED, ACTIVE, SUSPENDED
    oauthConnected: false,
    kycCompleted: false,
    balanceAdded: false,
    balances: [], // Array of balance objects with usd_total_amount
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [kycInitialized, setKycInitialized] = useState(false);
  const [kycCredentials, setKycCredentials] = useState(null);
  const [kycModalOpen, setKycModalOpen] = useState(false);

  // Check if we're coming back from OAuth callback
  const callbackStatus = searchParams.get("status");

  useEffect(() => {
    if (callbackStatus === "callback_success") {
      toast.success("Successfully connected to Airwallex!");
    }
    fetchSetupStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callbackStatus]);

  useEffect(() => {
    // Poll setup status every 30 seconds
    const interval = setInterval(() => {
      fetchSetupStatus();
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  // Reset actionLoading when page is restored from cache (browser back button)
  useEffect(() => {
    const handlePageShow = (event) => {
      // Check if page was restored from bfcache
      if (event.persisted) {
        setActionLoading(false);
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // Mount KYC element after container is rendered
  useEffect(() => {
    const mountKYC = async () => {
      if (!kycInitialized || !kycCredentials) return;

      try {
        const { authorization_code, code_verifier, client_id } = kycCredentials;

        // Initialize Airwallex SDK
        await Airwallex.init({
          clientId: client_id,
          env: AIRWALLEX_CONFIG.ENV,
          authCode: authorization_code,
          codeVerifier: code_verifier,
        });

        const kycElement = await Airwallex.createElement("kyc", {
          hideHeader: true,
          hideNav: true,
        });

        await kycElement.mount("kyc-container");

        kycElement.on("success", () => {
          toast.success("KYC submitted successfully!");
          setKycInitialized(false);
          setKycCredentials(null);
          setKycModalOpen(false);
          fetchSetupStatus();
        });

        kycElement.on("error", (error) => {
          toast.error("KYC verification failed: " + error.message);
          setError("KYC verification failed. Please try again.");
          setKycInitialized(false);
          setKycCredentials(null);
          setKycModalOpen(false);
        });

        kycElement.on("cancel", () => {
          setKycInitialized(false);
          setKycCredentials(null);
          setKycModalOpen(false);
        });

        setActionLoading(false);
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to mount KYC. Please try again.";
        setError(errorMessage);
        toast.error(errorMessage);
        setKycInitialized(false);
        setKycCredentials(null);
        setActionLoading(false);
      }
    };

    mountKYC();
  }, [kycInitialized, kycCredentials]);

  const fetchSetupStatus = async () => {
    try {
      const result = await getSetupStatus();

      // Handle HTTP 204 No Content - no account exists yet
      if (result.status === 204) {
        // Set empty setup status to show step 1
        setSetupStatus({
          status: null,
          oauthConnected: false,
          kycCompleted: false,
          balanceAdded: false,
          balances: [],
        });
      } else if (result.status === 200 && result.data) {
        setSetupStatus(result.data);

        // Calculate total balance
        const balances = result.data.balances || [];
        const totalBalance = balances.reduce((sum, balance) => {
          return sum + (parseFloat(balance.usd_total_amount) || 0);
        }, 0);

        // If account is ACTIVE and balance is sufficient, redirect to dashboard
        if (result.data.status === "ACTIVE" && totalBalance >= AIRWALLEX_CONFIG.MIN_BALANCE_THRESHOLD) {
          navigate("/");
        }
      }
    } catch (err) {
      console.error("Failed to fetch setup status:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartSetup = async (newToAirwallex = true) => {
    setActionLoading(true);
    setError("");

    try {
      const result = await initializeSetup(newToAirwallex);

      if (result.status === 200 && result.data?.authorizationUrl) {
        // Redirect to Airwallex OAuth page
        window.location.href = result.data.authorizationUrl;
      } else {
        throw new Error("Authorization URL not received");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to start setup. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
      setActionLoading(false);
    }
  };

  const handleInitializeKYC = async () => {
    setActionLoading(true);
    setError("");

    try {
      const result = await initializeKYC();

      if (result.status === 200 && result.data) {
        const { authorization_code, code_verifier } = result.data;

        // Use hardcoded Airwallex OAuth Client ID (from config)
        // Backend only provides authorization_code and code_verifier
        const airwallexClientId = AIRWALLEX_CONFIG.CLIENT_ID;

        // Store credentials and trigger KYC initialization in modal
        setKycCredentials({
          authorization_code,
          code_verifier,
          client_id: airwallexClientId
        });
        setKycInitialized(true);
        setKycModalOpen(true);
        // actionLoading will be set to false in the useEffect after mounting
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to initialize KYC. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
      setActionLoading(false);
    }
  };

  const handleCheckBalance = async () => {
    setActionLoading(true);
    setError("");

    try {
      const result = await checkBalance();

      if (result.status === 200 && result.data?.balanceAdded) {
        toast.success("Wallet setup completed successfully!");
        fetchSetupStatus();

        // Navigate to dashboard after successful setup
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        setError(
          "No balance detected. Please add funds to your Airwallex wallet."
        );
        toast.warning("Please add funds to complete setup");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        "Failed to check balance. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddFunds = () => {
    // Construct the URL from base URL + extended path
    const baseUrl = AIRWALLEX_CONFIG.DEMO_URL;
    const extendedPath = "/app/add-funds";
    const fullUrl = `${baseUrl}${extendedPath}`;

    // Open in new tab
    window.open(fullUrl, "_blank", "noopener,noreferrer");
  };

  const handleModalClose = () => {
    setKycModalOpen(false);
    setKycInitialized(false);
    setKycCredentials(null);
    // Fetch updated account status after modal closes
    fetchSetupStatus();
  };

  const getProgressPercentage = () => {
    let progress = 0;
    if (setupStatus.oauthConnected) progress += 33;
    if (setupStatus.kycCompleted) progress += 33;
    if (setupStatus.balanceAdded) progress += 34;
    return progress;
  };

  const getTotalBalance = () => {
    if (!setupStatus.balances || setupStatus.balances.length === 0) return 0;
    return setupStatus.balances.reduce((sum, balance) => {
      return sum + (parseFloat(balance.usd_total_amount) || 0);
    }, 0);
  };

  const getCurrentStep = () => {
    const { status } = setupStatus;
    const totalBalance = getTotalBalance();

    // If status is ACTIVE and balance is sufficient, all steps are complete
    if (status === "ACTIVE" && totalBalance >= AIRWALLEX_CONFIG.MIN_BALANCE_THRESHOLD) return 4;

    // If status is ACTIVE but balance is insufficient, show step 3
    if (status === "ACTIVE" && totalBalance < AIRWALLEX_CONFIG.MIN_BALANCE_THRESHOLD) return 3;

    // If OAuth not connected and no status, step 1
    const isOAuthConnected = setupStatus.oauthConnected ||
                              status === "CREATED" ||
                              status === "SUBMITTED" ||
                              status === "ACTION_REQUIRED" ||
                              status === "ACTIVE";

    if (!isOAuthConnected) return 1;

    // Based on account status
    if (status === "CREATED") return 2; // KYC needs to be submitted
    if (status === "SUBMITTED") return 2; // KYC submitted, waiting for approval
    if (status === "ACTION_REQUIRED") return 2; // KYC needs resubmission
    if (setupStatus.kycCompleted && !setupStatus.balanceAdded) return 3;

    return 4; // All complete
  };

  const getStepStatus = () => {
    const { status } = setupStatus;
    const totalBalance = getTotalBalance();

    // If status exists (CREATED, SUBMITTED, ACTION_REQUIRED, ACTIVE, SUSPENDED), OAuth is connected
    const isOAuthConnected = setupStatus.oauthConnected ||
                              status === "CREATED" ||
                              status === "SUBMITTED" ||
                              status === "ACTION_REQUIRED" ||
                              status === "ACTIVE";

    return {
      step1: {
        completed: isOAuthConnected,
        label: "Create an Airwallex account & connect to Hotel Trader",
        description: "Sign up for Airwallex and link it to the Hotel Trader account"
      },
      step2: {
        completed: status === "ACTIVE" || setupStatus.kycCompleted,
        inProgress: status === "SUBMITTED",
        actionRequired: status === "ACTION_REQUIRED",
        pending: status === "CREATED",
        label: "Complete KYC Process",
        description: "Complete pending tasks in Airwallex to verify identity and business info"
      },
      step3: {
        completed: status === "ACTIVE" && totalBalance >= AIRWALLEX_CONFIG.MIN_BALANCE_THRESHOLD,
        label: "Add Funds",
        description: "Deposit funds to start using your wallet"
      }
    };
  };

  if (loading) {
    return (
      <div className="wallet-setup-container">
        <div className="text-center p-5">
          <Spinner color="primary" />
          <p className="mt-3">Loading setup status...</p>
        </div>
      </div>
    );
  }

  const currentStep = getCurrentStep();
  const progressPercentage = getProgressPercentage();
  const stepStatus = getStepStatus();
  const totalBalance = getTotalBalance();

  // If account is ACTIVE and balance is sufficient, show dashboard (should redirect, but just in case)
  if (setupStatus.status === "ACTIVE" && totalBalance >= AIRWALLEX_CONFIG.MIN_BALANCE_THRESHOLD) {
    return (
      <div className="wallet-setup-container">
        <Card className="setup-card">
          <CardBody>
            <div className="text-center p-5">
              <h2>Wallet Dashboard</h2>
              <p className="text-muted mt-3">
                Dashboard is in progress by another developer
              </p>
              <Alert color="success" className="mt-4">
                Your wallet setup is complete and active!
              </Alert>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  // If account is SUSPENDED, show error message
  if (setupStatus.status === "SUSPENDED") {
    return (
      <div className="wallet-setup-container">
        <Card className="setup-card">
          <CardBody>
            <Alert color="danger">
              <h4>Account Suspended</h4>
              <p>
                Your Airwallex account is currently suspended. Wallet balance checks and payments are currently unavailable. Please contact Airwallex or Hotel Trader Support for assistance.
              </p>
            </Alert>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="wallet-setup-container">
      <Card className="setup-card">
        <CardBody>
          <h2 className="setup-title">Wallet by Airwallex</h2>
          <p className="setup-subtitle">
            A New Hotel Trader Payment Experience
          </p>
          <p className="setup-description">
            Secure multi-currency wallet solution for seamless hotel booking operations
          </p>

          <Alert color="warning" className="important-notes">
            <div className="alert-content">
              <h5>
                <i className="fas fa-exclamation-triangle"></i>
                Important Notes
              </h5>
              <p className="mb-0">
                Ensure you have all required business documents ready. Check
                required documents for your region in the{" "}
                <a
                  href="https://help.airwallex.com/hc/en-gb/search?utf8=✓&query=documents+required"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Airwallex Help Center
                </a>
                . Verification process typically takes 1-3 business days.
              </p>
            </div>
          </Alert>

          {error && (
            <Alert color="danger" toggle={() => setError("")}>
              {error}
            </Alert>
          )}

          <div className="steps-section">
            <h3 className="steps-heading">Set up your wallet in 3 easy steps!</h3>

            <div className="setup-steps-visual">
            {/* Step 1: OAuth Connection */}
            <div
              className={`step-card ${stepStatus.step1.completed ? "completed" : ""}`}
            >
              <div className="step-icon">
                {stepStatus.step1.completed ? (
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#4caf50"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9 12l2 2 4-4"></path>
                  </svg>
                ) : (
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 40 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.1688 19H10.8313C4.85063 19 0 23.85 0 29.8312C0 31.0312 0.97 32 2.16625 32H25.835C27.0312 32 28 31.0312 28 29.8312C28 23.85 23.15 19 17.1688 19ZM25.8312 30H2.16625C2.075 30 2 29.925 2 29.8312C2 24.9625 5.9625 21 10.8313 21H17.1688C22.0375 21 26 24.9625 26 29.8312C26 29.925 25.925 30 25.8312 30ZM14 16C18.4188 16 22 12.4181 22 8C22 3.58187 18.4188 0 14 0C9.58125 0 6 3.58187 6 8C6 12.4181 9.58125 16 14 16ZM14 2C17.3088 2 20 4.69125 20 8C20 11.3081 17.3088 14 14 14C10.6912 14 8 11.3062 8 8C8 4.69125 10.6938 2 14 2ZM39 13H35V9C35 8.45 34.55 8 34 8C33.45 8 33 8.44725 33 9V13H29C28.45 13 28 13.45 28 14C28 14.55 28.4473 15 29 15H33V19C33 19.5527 33.4473 20 34 20C34.5527 20 35 19.5527 35 19V15H39C39.55 15 40 14.55 40 14C40 13.45 39.55 13 39 13Z"
                      fill="#FF5A37"
                    />
                  </svg>
                )}
              </div>
              <h4>{stepStatus.step1.label}</h4>
              <p>{stepStatus.step1.description}</p>
            </div>

            {/* Step 2: KYC Verification */}
            <div
              className={`step-card ${
                stepStatus.step2.completed
                  ? "completed"
                  : stepStatus.step2.inProgress
                    ? "in-progress"
                    : stepStatus.step2.actionRequired
                      ? "action-required"
                      : ""
              }`}
            >
              <div className="step-icon">
                {stepStatus.step2.completed ? (
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#4caf50"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9 12l2 2 4-4"></path>
                  </svg>
                ) : (
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 30 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M28.5391 9.78906L20.2188 1.46875C19.2734 0.526719 18.0078 0 16.6797 0H5C2.23828 0 0 2.23828 0 5L0.000507812 35C0.000507812 37.7617 2.23879 40 5.00051 40H25C27.7617 40 30 37.7617 30 35V13.3203C30 11.9922 29.4766 10.7266 28.5391 9.78906ZM17.5 2.6625C17.8498 2.78484 18.177 2.96297 18.4469 3.23273L26.7672 11.553C27.0391 11.8203 27.2188 12.1484 27.3359 12.5H18.75C18.0625 12.5 17.5 11.9375 17.5 11.25V2.6625ZM27.5 35C27.5 36.3781 26.3781 37.5 25 37.5H5C3.62188 37.5 2.5 36.3781 2.5 35V5C2.5 3.62188 3.62188 2.5 5 2.5H15V11.25C15 13.3203 16.6797 15 18.75 15H27.5V35ZM9.63281 24.9531C9.14453 24.4648 8.35312 24.4648 7.86562 24.9531C7.37813 25.4414 7.37734 26.2328 7.86562 26.7203L12.0328 30.8867C12.2812 31.125 12.6016 31.25 12.9141 31.25C13.2266 31.25 13.5538 31.128 13.7977 30.8838L22.1336 22.5478C22.6219 22.0595 22.6219 21.2681 22.1336 20.7806C21.6453 20.2931 20.8539 20.2923 20.3664 20.7806L12.918 28.2306L9.63281 24.9531Z"
                      fill="#FF5A37"
                    />
                  </svg>
                )}
              </div>
              <h4>{stepStatus.step2.label}</h4>
              <p>{stepStatus.step2.description}</p>

              {stepStatus.step2.inProgress && (
                <div className="status-badge badge bg-info mt-2">
                  KYC Submitted - Under Review
                </div>
              )}
              {stepStatus.step2.actionRequired && (
                <div className="status-badge badge bg-warning mt-2">
                  Action Required - Resubmit KYC
                </div>
              )}
            </div>

            {/* Step 3: Add Funds */}
            <div
              className={`step-card ${stepStatus.step3.completed ? "completed" : ""}`}
            >
              <div className="step-icon">
                {stepStatus.step3.completed ? (
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#4caf50"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9 12l2 2 4-4"></path>
                  </svg>
                ) : (
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 40 35"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M36.25 0C36.9375 0 37.5 0.559375 37.5 1.25C37.5 1.94062 36.9375 2.5 36.25 2.5H6.25C4.17891 2.5 2.5 4.17891 2.5 6.25V28.75C2.5 30.8203 4.17891 32.5 6.25 32.5H33.75C35.8203 32.5 37.5 30.8203 37.5 28.75V11.25C37.5 9.17969 35.8203 7.5 33.75 7.5H8.75C8.0625 7.5 7.5 6.9375 7.5 6.25C7.5 5.5625 8.0625 5 8.75 5H33.75C37.2031 5 40 7.79688 40 11.25V28.75C40 32.2031 37.2031 35 33.75 35H6.25C2.79844 35 0 32.2031 0 28.75V6.25C0 2.79844 2.79844 0 6.25 0H36.25ZM28.125 20C28.125 18.9609 28.9609 18.125 30 18.125C31.0391 18.125 31.875 18.9609 31.875 20C31.875 21.0391 31.0391 21.875 30 21.875C28.9609 21.875 28.125 21.0391 28.125 20Z"
                      fill="#FF5A37"
                    />
                  </svg>
                )}
              </div>
              <h4>{stepStatus.step3.label}</h4>
              <p>{stepStatus.step3.description}</p>
            </div>
          </div>

          <div className="cta-section">
            {currentStep === 1 && !setupStatus.status && (
              <>
                <Button
                  color="primary"
                  size="lg"
                  onClick={() => handleStartSetup(true)}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Starting...
                    </>
                  ) : (
                    "Create New AirWallex Account"
                  )}
                </Button>
                <div className="existing-user-section">
                  <p className="existing-user-text">
                    Already have an AirWallex account?{" "}
                    <button
                      className="connect-link"
                      onClick={() => handleStartSetup(false)}
                      disabled={actionLoading}
                    >
                      Link Existing AirWallex Account
                    </button>
                  </p>
                </div>
              </>
            )}

            {currentStep === 2 && setupStatus.status === "CREATED" && (
              <Button
                color="primary"
                size="lg"
                onClick={handleInitializeKYC}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Initializing...
                  </>
                ) : (
                  "Continue to KYC"
                )}
              </Button>
            )}

            {currentStep === 2 && setupStatus.status === "SUBMITTED" && (
              <Alert color="info">
                <h5>KYC Under Review</h5>
                <p className="mb-0">
                  Your KYC application is being reviewed by Airwallex. This
                  typically takes 1-3 business days. We&apos;ll update the status
                  automatically.
                </p>
              </Alert>
            )}

            {currentStep === 2 && setupStatus.status === "ACTION_REQUIRED" && (
              <Button
                color="warning"
                size="lg"
                onClick={handleInitializeKYC}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Initializing...
                  </>
                ) : (
                  "Resubmit KYC"
                )}
              </Button>
            )}

            {currentStep === 3 && setupStatus.status === "ACTIVE" && (
              <Button
                color="primary"
                size="lg"
                onClick={handleAddFunds}
              >
                Continue to Add Funds
              </Button>
            )}

          </div>
          </div>

          {/* KYC Modal */}
          <Modal
            isOpen={kycModalOpen}
            toggle={handleModalClose}
            size="lg"
            centered
            backdrop="static"
            style={{ maxWidth: '800px' }}
            contentClassName="kyc-modal-content"
          >
            <ModalHeader toggle={handleModalClose}>
              Complete KYC Verification
            </ModalHeader>
            <ModalBody style={{ minHeight: '60vh', maxHeight: '70vh', overflowY: 'auto', padding: 0, display: 'flex', flexDirection: 'column' }}>
              {kycInitialized && (
                <div
                  id="kyc-container"
                  className="kyc-container"
                  style={{
                    minHeight: '60vh',
                    height: '100%',
                    border: 'none',
                    borderRadius: 0,
                    padding: 0,
                    margin: 0,
                    maxWidth: '100%',
                    boxShadow: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1
                  }}
                ></div>
              )}
            </ModalBody>
          </Modal>

          {/* Features Section */}
          <div className="features-section mt-5">
            <div className="feature-card">
              <div className="feature-icon">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 29 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M28.6838 15.4375C29.0638 14.8638 29.1039 14.1188 28.79 13.5039C28.4799 12.8828 27.8606 12.5 27.1884 12.5H15.3615L19.2011 2.57031C19.4253 1.99414 19.3568 1.3375 19.0194 0.821875C18.6819 0.30625 18.0576 0 17.5214 0H4.2293C3.32075 0 2.55343 0.695781 2.43335 1.62812L0.0165955 20.3781C-0.054238 20.9141 0.103901 21.4531 0.448439 21.8594C0.792977 22.2656 1.29068 22.5 1.81254 22.5H12.8616L9.70626 37.6641C9.52455 38.5406 9.96825 39.4266 10.7636 39.7805C10.9962 39.9531 11.2379 40 11.4796 40C12.0625 40 12.629 39.707 12.9757 39.1844L28.6838 15.4375ZM14.357 20H2.50358L4.75947 2.5H16.6318L12.4538 13.2891C12.1366 14.1094 12.7257 15 13.5791 15H26.0405L12.7106 35.0859L15.5374 21.4453C15.7013 20.7344 15.1273 20 14.357 20Z"
                    fill="#FF5A37"
                  />
                </svg>
              </div>
              <h4>Faster & Transparent Transactions</h4>
              <p>
                Instant balance checks and booking-level transactions for
                clarity
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 40 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M28 10H4C2.89563 10 2 10.8938 2 12V28C2 29.1063 2.89563 30 4 30H22.5125C23.1438 30.7563 23.8687 31.4312 24.675 32H4C1.79063 32 0 30.2062 0 28V12C0 9.79375 1.79063 8 4 8H28C30.2062 8 32 9.79375 32 12V12.0437C31.6688 12.0125 31.3375 12 31 12C30.6625 12 30.3312 12.0125 30 12.0437V12C30 10.8938 29.1063 10 28 10ZM28 4C28.55 4 29 4.4475 29 5C29 5.5525 28.55 6 28 6H4C3.4475 6 3 5.5525 3 5C3 4.4475 3.4475 4 4 4H28ZM25 0C25.55 0 26 0.44775 26 1C26 1.5525 25.55 2 25 2H7C6.45 2 6 1.5525 6 1C6 0.44775 6.45 0 7 0H25ZM32 21.9438H35C35.55 21.9438 36 22.45 36 22.9438C36 23.55 35.55 23.9438 35 23.9438H32V26.9438C32 27.55 31.55 27.9438 31 27.9438C30.45 27.9438 30 27.55 30 26.9438V23.9438H27C26.45 23.9438 26 23.55 26 22.9438C26 22.45 26.45 21.9438 27 21.9438H30V18.9438C30 18.45 30.45 17.9438 31 17.9438C31.55 17.9438 32 18.45 32 18.9438V21.9438ZM40 23C40 27.9688 35.9688 32 31 32C26.0312 32 22 27.9688 22 23C22 18.0312 26.0312 14 31 14C35.9688 14 40 18.0312 40 23ZM31 16C27.1313 16 24 19.1313 24 23C24 26.8687 27.1313 30 31 30C34.8688 30 38 26.8687 38 23C38 19.1313 34.8688 16 31 16Z"
                    fill="#FF5A37"
                  />
                </svg>
              </div>
              <h4>Multi-Currency Support</h4>
              <p>
                Minimize FX fees, lock in favorable rates, and expand globally
                with ease
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 40 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22.2222 33.75C22.3125 33.75 22.4028 33.743 22.4931 33.7359C23.0417 34.3406 23.6458 34.9242 24.3194 35.4656C23.6944 35.8102 22.9792 36 22.2222 36H4.44444C1.98958 36 0 33.982 0 31.5V4.5C0 2.01445 1.98958 0 4.44444 0H15.2847C16.1667 0 17.0208 0.35557 17.6458 0.988594L25.6875 9.13359C26.3125 9.76641 26.6667 10.6312 26.6667 11.5242V14.5547L24.4444 15.4547V13.5H16.6667C14.8264 13.5 13.3333 11.9883 13.3333 10.125V2.25H4.44444C3.21736 2.25 2.22222 3.25758 2.22222 4.5V31.5C2.22222 32.7445 3.21736 33.75 4.44444 33.75H22.2222ZM24.1181 10.7297L16.0694 2.57977C15.9306 2.43422 15.75 2.33297 15.4931 2.28375V10.125C15.4931 10.7437 16.0556 11.25 16.6042 11.25H24.4097C24.3611 11.0531 24.2639 10.8703 24.1181 10.7297ZM38.9514 19.2445C39.5833 19.5047 40 20.1234 40 20.8125C40 25.2633 38.2014 32.6813 30.6389 35.8734C30.2292 36.0422 29.7708 36.0422 29.3611 35.8734C21.7986 32.6813 19.9375 25.2633 19.9375 20.8125C19.9375 20.1234 20.3542 19.5047 21.0486 19.2445L29.3819 15.8695C29.7778 15.7078 30.2222 15.7078 30.6181 15.8695L38.9514 19.2445ZM28.8889 33.1453V18.4922L22.2292 21.1922C22.3194 24.9117 23.7917 30.2977 28.8889 33.1453ZM31.1111 18.4922V33.1453C36.2083 30.2977 37.6806 24.9117 37.7708 21.1922L31.1111 18.4922Z"
                    fill="#FF5A37"
                  />
                </svg>
              </div>
              <h4>Protected Funds</h4>
              <p>
                Safeguarded by Airwallex, fully compliant under EMI/PSP
                regulations
              </p>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default WalletSetup;
